import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import type { IconProps } from "mds/utils/images";

export type IconTextProps = IconProps & {
  text?: ReactNode;
  iconStart?: ReactNode;
  iconEnd?: ReactNode;
  className?: string;
  hideIconOnSm?: boolean;
  hideIconOnMd?: boolean;
  hideTextOnSm?: boolean;
  truncateText?: boolean;
  // If container query class names are provided, the short text (if provided) will be
  // displayed when the container query is met, and any icons will be hidden as well.
  // The class names have to be passed in like this, otherwise tailwind will not compile them.
  containerQueryClassNameHide?: string; // Example: @width[200px]/container-name:hidden
  containerQueryClassNameShow?: string; // Example: @width[200px]/container-name:inline
  shortText?: string;
};

export const IconText: FC<IconTextProps> = ({
  className,
  iconStart,
  iconEnd,
  text,
  hideIconOnSm,
  hideIconOnMd,
  hideTextOnSm,
  truncateText,
  shortText,
  containerQueryClassNameHide,
  containerQueryClassNameShow,
}) => {
  // If both text and shortText are provided, containerQueryClassNameHide and containerQueryClassNameShow
  // also need to be provided. In this case, we render both versions of the text and use container queries
  // to show/hide the appropriate version of the text depending on the size of the container.
  const textElement =
    typeof text === "string" ? (
      <>
        <span
          className={twMerge(
            truncateText ? "min-w-0 truncate" : "",
            hideTextOnSm ? "hidden sm:inline" : "",
            containerQueryClassNameShow && shortText ? "hidden" : "",
            containerQueryClassNameShow,
          )}
        >
          {text}
        </span>
        {containerQueryClassNameHide && shortText && (
          <span
            className={twMerge(
              truncateText ? "min-w-0 truncate" : "",
              hideTextOnSm ? "hidden sm:inline" : "",
              containerQueryClassNameHide,
            )}
          >
            {shortText}
          </span>
        )}
      </>
    ) : (
      text
    );

  const IconStartElement = iconStart ? (
    <span
      className={twMerge(
        hideIconOnSm ? "hidden md:inline" : hideIconOnMd ? "hidden lg:inline" : "",
        containerQueryClassNameShow ? "hidden" : "",
        containerQueryClassNameShow,
      )}
    >
      {iconStart}
    </span>
  ) : null;

  const IconEndElement = iconEnd ? (
    <span
      className={twMerge(
        hideIconOnSm ? "hidden md:inline" : hideIconOnMd ? "hidden lg:inline" : "",
        containerQueryClassNameShow ? "hidden" : "",
        containerQueryClassNameShow,
      )}
    >
      {iconEnd}
    </span>
  ) : null;

  return (
    <div className={twMerge("flex items-center gap-2", truncateText && "min-w-0", className)}>
      {IconStartElement}
      {textElement}
      {IconEndElement}
    </div>
  );
};
